import React, { useEffect, useId, useState } from "react";
import { useDispatch } from "react-redux";
import { styled } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { setDarkMode } from "@/store/customizer/CustomizerSlice";
import { setCookie, getCookie, deleteCookie } from "cookies-next";
import BedtimeIcon from "@mui/icons-material/Bedtime";
import LightModeIcon from "@mui/icons-material/LightMode";
import ContrastIcon from "@mui/icons-material/Contrast";
import CheckIcon from "@mui/icons-material/Check";

const appearanceOptions = [
  { value: "auto", label: "System", Icon: ContrastIcon },
  { value: "light", label: "Light", Icon: LightModeIcon },
  { value: "dark", label: "Dark", Icon: BedtimeIcon },
];

const ThemeButton = styled(IconButton)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  width: 44,
  height: 44,
  transition: "all 0.3s ease",
  color: theme.palette.grey[500],
  backgroundColor: "transparent",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.grey[600],
  },
  "&.Mui-focusVisible": {
    outline: `2px solid ${theme.palette.text.primary}`,
    outlineOffset: 2,
  },
}));

const ThemeToggle = () => {
  const dispatch = useDispatch();
  const [themeState, setThemeState] = useState("auto");
  const [menuAnchor, setMenuAnchor] = useState(null);
  const menuId = useId();
  const buttonId = useId();

  useEffect(() => {
    const oldThemeCookie = getCookie("theme");
    if (oldThemeCookie) {
      deleteCookie("theme");
    }

    const userPreference = getCookie("theme-preference");

    if (!userPreference) {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

      const prefersDark = mediaQuery.matches;
      const systemTheme = prefersDark ? "dark" : "light";

      dispatch(setDarkMode(systemTheme));

      setThemeState("auto");
    } else {
      dispatch(setDarkMode(userPreference));
      setThemeState(userPreference);
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (e) => {
      const userPreference = getCookie("theme-preference");

      if (!userPreference) {
        const newTheme = e.matches ? "dark" : "light";
        dispatch(setDarkMode(newTheme));
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [dispatch]);

  const handleAppearanceChange = (preference) => {
    if (preference === "auto") {
      deleteCookie("theme-preference", { path: "/" });
      const prefersDark = window.matchMedia(
        "(prefers-color-scheme: dark)",
      ).matches;
      dispatch(setDarkMode(prefersDark ? "dark" : "light"));
    } else {
      setCookie("theme-preference", preference, {
        maxAge: 60 * 60 * 24 * 365,
        secure: process.env.NODE_ENV === "production",
        sameSite: "lax",
        path: "/",
      });
      dispatch(setDarkMode(preference));
    }
    setThemeState(preference);
    setMenuAnchor(null);
  };

  const selectedAppearance = appearanceOptions.find(
    (option) => option.value === themeState,
  ) || appearanceOptions[0];
  const AppearanceIcon = selectedAppearance.Icon;

  return (
    <>
      <Tooltip
        title={menuAnchor ? "" : "Change appearance"}
        describeChild
        arrow
        placement="bottom"
      >
        <ThemeButton
          id={buttonId}
          aria-label={`Change appearance, current setting: ${selectedAppearance.label}`}
          aria-haspopup="menu"
          aria-controls={menuAnchor ? menuId : undefined}
          aria-expanded={Boolean(menuAnchor)}
          onClick={(event) => setMenuAnchor(event.currentTarget)}
        >
          <AppearanceIcon sx={{ fontSize: 24 }} />
        </ThemeButton>
      </Tooltip>
      <Menu
        id={menuId}
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        disableAutoFocusItem={false}
        disableRestoreFocus={false}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          list: { "aria-labelledby": buttonId },
          paper: { sx: { minWidth: 180, mt: 0.5 } },
          // Preserve keyboard focus rather than using the global menu blur handlers.
          transition: { onExit: () => {}, onExited: () => {} },
        }}
      >
        {appearanceOptions.map(({ value, label, Icon }) => (
          <MenuItem
            key={value}
            selected={themeState === value}
            role="menuitemradio"
            aria-checked={themeState === value}
            onClick={() => handleAppearanceChange(value)}
          >
            <ListItemIcon sx={{ color: "text.secondary" }}>
              <Icon sx={{ fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText primary={label} />
            {themeState === value && (
              <CheckIcon sx={{ fontSize: 18, ml: 2, color: "text.primary" }} />
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default ThemeToggle;
